import React, { useState, useEffect, useRef } from "react";
import WebViewer, { WebViewerInstance } from "@pdftron/webviewer";
import { AppConfiguration, dayjs, LocalAccountStorage, Message, PDFTRON_WEBVIEWER_OPTIONS, SettingsErrorMessages, SettingsSuccessMessages, useAppDispatch, FileTemplates } from "src/common";
import { accountActions, uiActions } from "src/store";
import { saveAs } from "file-saver";

type AllProps = {
  recovery_shard: string;
  user_id: string;
  downloadComplete: Function;
};

/* Description: PDF generator for the user shard (part of the users key) ** FOR PASSWORD RECOVERY */
function UserShardPDFGeneratorComponent(props: AllProps) {
  const { recovery_shard, user_id, downloadComplete } = props;
  const file_name = `password-recovery-file-${dayjs().local().format("MM-DD-YYYY-h:mm")}.pdf`;
  const dispatch = useAppDispatch();
  const [instance, setInstance] = useState<WebViewerInstance | undefined>(undefined);
  const viewer = useRef<HTMLDivElement>(null);

  /* Description: on initial load, get the qr code and instantiate a new instance of web viewer.
  We use a template for the initial doc which we will then modify with the current users backup information */
  useEffect(() => {
    (async () => {

      if (!!viewer) {
        const _instance = await WebViewer(
          Object.assign({}, PDFTRON_WEBVIEWER_OPTIONS, {
            initialDoc: FileTemplates.account_recovery,
          }),
          viewer.current as HTMLDivElement,
        );
        const { documentViewer, PDFNet } = _instance.Core;
        // NOTE: Disable js embeds for CSP 
        AppConfiguration.buildForWeb() && _instance.Core.disableEmbeddedJavaScript();
        documentViewer.addEventListener("documentLoaded", async () => {
          await PDFNet.runWithCleanup(async () => {
            await modifyPDF(_instance);
          }, PDFTRON_WEBVIEWER_OPTIONS.licenseKey);
        });

      }
    })();
  }, []);

  /* Description: cleanup function, disposes the current documents data  */
  useEffect(() => {
    return () => {
      instance?.Core.documentViewer.dispose();
    };
  }, [instance]);

  /* Description: Notify the store that the backup pdf file has been downloaded and call the getBackup function again. */
  function complete() {
    LocalAccountStorage.removeTemporaryCookies("express_recovery_not_setup", "false");
    dispatch(accountActions.setExpressRecoveryNotSetup(null));
    downloadComplete();
    dispatch(uiActions.handleSetMessage(new Message(SettingsSuccessMessages.successfully_downloaded_file)));
  };

  /* Description: create and modify the initial template with the users backup info.
     NOTE: DA-8023 — The recovery_code field value is set via PDFNet XFDF merge + flatten
     instead of the annotation manager's field.setValue(). This bypasses a WebViewer 11.10+
     regression where multiline fields with long unbroken strings render as a single-character
     column. By merging a value-only XFDF (no <annotations>) and flattening at the PDFNet level,
     we get a single clean text layer with correct wrapping. */
  async function modifyPDF(instance: WebViewerInstance) {
    const { documentViewer, PDFNet } = instance.Core;
    await PDFNet.initialize();
    const doc = await documentViewer.getDocument().getPDFDoc();

    try {
      // lock the document before a write operation
      await doc.lock();

      const replacer = await PDFNet.ContentReplacer.create();
      const page = await doc.getPage(1);

      // replace a text placeholder
      await replacer.addString("email_address", user_id);
      await replacer.process(page);

      // Set recovery_code field value via FDF merge (value only, no widget annotations)
      const xfdf = `<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/"><fields><field name="recovery_code"><value>${recovery_shard}</value></field></fields></xfdf>`;
      const fdf_doc = await PDFNet.FDFDoc.createFromXFDF(xfdf);
      await doc.fdfMerge(fdf_doc);

      // Flatten form fields to static content and save
      await doc.flattenAnnotations(true);
      const buf = await doc.saveMemoryBuffer(PDFNet.SDFDoc.SaveOptions.e_linearized);

      const blob = new Blob([new Uint8Array(buf)], { type: "application/pdf" });
      saveAs(blob, file_name);
      complete();
      setInstance(instance);
    } catch (e: unknown) {
      console.error(e);
      dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_generating_pdf)));
    }
  }


  return <div className="opacity-0" ref={viewer}></div>;
}

export default React.memo(UserShardPDFGeneratorComponent);
